// Stage 90: Parallax Inspector — live current-slot pane.
//
// Renders the CURRENT cup reading for `currentConversationId` by polling
// /api/parallax/current-slot at 1500 ms intervals via useParallaxSlot. No
// history, no accumulation — the inspector reflects the live slot only
// (TINS §2.5 + §Implementation Notes #6: no force-re-read button, no
// "previous cups" view).
//
// IMPORTANT (TINS §Implementation Notes #4): the "View raw parallax.md"
// button is gated behind import.meta.env.DEV in production so rawParallaxMd
// is never accessible to non-developer operators on a prod deploy. Even
// when shown, the raw text lives only inside an expandable <details>
// element — it is never rendered into the chat itself, by design and by
// boundary verifier assertion (ChatTranscript.tsx may not import api/parallax).
//
// Mounted inside the SettingsModal Parallax tab. The empty-state message
// distinguishes "no conversation selected" (mount-time, before chat opens)
// from "no slot yet" (chat is open but no mood shift has fired).

import { useState } from 'react';
import { useParallaxSlot } from '../hooks/useParallaxSlot';

function fmtTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
}

export function ParallaxInspector({
  currentConversationId,
}: {
  currentConversationId?: string | null;
}) {
  const { slot, loading, error } = useParallaxSlot(
    currentConversationId ?? null,
  );
  const [rawOpen, setRawOpen] = useState(false);

  if (!currentConversationId) {
    return (
      <div
        style={{
          padding: '12px 14px',
          background: 'var(--panel)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          color: 'var(--muted)',
          fontSize: 13,
        }}
        aria-live="polite"
      >
        No active conversation. Open a chat to see live cup readings here.
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '12px 14px',
          background: 'var(--panel)',
          border: '1px solid var(--border)',
          borderLeft: '3px solid var(--danger)',
          borderRadius: 8,
          color: 'var(--danger)',
          fontSize: 13,
        }}
        aria-live="polite"
      >
        Inspector poll failed: {error}
      </div>
    );
  }

  if (!slot) {
    return (
      <div
        style={{
          padding: '12px 14px',
          background: 'var(--panel)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          color: 'var(--muted)',
          fontSize: 13,
        }}
        aria-live="polite"
      >
        {loading
          ? 'Loading…'
          : 'No current cup — waiting for the first mood shift.'}
      </div>
    );
  }

  const r = slot.result;
  const isSplash = r.cupState === 'SPLASH';
  const accentColor = isSplash ? 'var(--warning)' : 'var(--accent)';
  const statusBg = isSplash ? '#f97316' : '#3b82f6';

  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'var(--panel)',
        border: '1px solid var(--border)',
        borderLeft: `3px solid ${accentColor}`,
        borderRadius: 8,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <strong style={{ color: 'var(--fg)' }}>
          Current cup: <code>{r.cupReadId.slice(0, 8)}</code>
        </strong>
        <span style={{ color: 'var(--muted)', fontSize: 12 }}>
          read {fmtTime(r.generatedAt)}
        </span>
      </div>

      <div style={{ marginTop: 6, color: 'var(--fg)' }}>
        Mood region: <strong>{r.moodLabel}</strong>
      </div>

      <div style={{ marginTop: 8 }}>
        <span
          aria-label={r.cupState.toLowerCase()}
          style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: 4,
            background: statusBg,
            color: 'white',
            fontWeight: 700,
            letterSpacing: 0.5,
            fontSize: 12,
          }}
        >
          {r.cupState}
        </span>
      </div>

      {isSplash ? (
        <div
          style={{
            marginTop: 10,
            padding: '8px 10px',
            background: 'var(--panel-2)',
            border: '1px solid var(--border-soft)',
            borderRadius: 6,
          }}
        >
          <div style={{ color: 'var(--warning)' }}>
            Splashing against:{' '}
            <em style={{ fontStyle: 'normal', fontWeight: 600 }}>
              &ldquo;{r.splashAnchor ?? '(unspecified)'}&rdquo;
            </em>
          </div>
          <div style={{ color: 'var(--warning)', marginTop: 4 }}>
            Direction:{' '}
            <em style={{ fontStyle: 'normal' }}>
              {r.splashDirection ?? '(unspecified)'}
            </em>
          </div>
        </div>
      ) : null}

      {r.trajectorySummary ? (
        <div style={{ marginTop: 10, color: 'var(--muted)', fontStyle: 'italic' }}>
          Trajectory: {r.trajectorySummary}
        </div>
      ) : null}

      <div style={{ marginTop: 10 }}>
        <strong style={{ fontSize: 12, color: 'var(--muted)' }}>
          Anchors in use ({r.anchorsUsed.length}):
        </strong>
        <ul
          role="list"
          style={{
            margin: '4px 0 0 0',
            paddingLeft: 18,
            color: 'var(--fg)',
            fontSize: 12,
          }}
        >
          {r.anchorsUsed.map((a, i) => (
            <li key={i} role="listitem">
              &ldquo;{a.text}&rdquo;
            </li>
          ))}
        </ul>
      </div>

      <div
        style={{
          marginTop: 10,
          fontSize: 11,
          color: 'var(--muted)',
        }}
      >
        Last cup-read: {r.durationMs} ms ({r.modelUsed} @ T={
        /* temperature is fixed in v1 — pref-store enforces the 0.6 cap */
        '0.4'} ·{' '}
        {r.mode} mode)
      </div>

      {import.meta.env.DEV ? (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            onClick={() => setRawOpen((v) => !v)}
            style={{
              padding: '4px 10px',
              fontSize: 12,
              background: 'var(--panel-2)',
              border: '1px solid var(--border)',
              color: 'var(--fg)',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            {rawOpen ? 'Hide raw parallax.md' : 'View raw parallax.md'}
          </button>
          {rawOpen ? (
            <details open style={{ marginTop: 6 }}>
              <summary style={{ fontSize: 12, color: 'var(--muted)' }}>
                rawParallaxMd (operator-only — never rendered in chat)
              </summary>
              <pre
                style={{
                  marginTop: 6,
                  padding: 8,
                  background: 'var(--bg)',
                  color: 'var(--fg)',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  fontSize: 11,
                  overflowX: 'auto',
                  maxHeight: 320,
                }}
              >
                {r.rawParallaxMd || '(empty)'}
              </pre>
            </details>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
